import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, MessageSquareText, Newspaper, PhoneCall, RotateCcw, RotateCw, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";

const initialGalleryForm = {
  title: "",
  location: "Toronto",
  category: "interior",
  room_type: "Living room",
  description: "",
  image_url: "",
  image_file_id: null,
  before_image_url: "",
  before_image_file_id: null,
  after_image_url: "",
  after_image_file_id: null,
  featured: false,
  accent: "Fresh project",
};

const SLOT_FIELDS = {
  image: { urlKey: "image_url", fileIdKey: "image_file_id", label: "Main image" },
  before_image: { urlKey: "before_image_url", fileIdKey: "before_image_file_id", label: "Before image" },
  after_image: { urlKey: "after_image_url", fileIdKey: "after_image_file_id", label: "After image" },
};

const buildVersionedUrl = (url, version) => (url && version ? `${url}?v=${version}` : url);

const UploadSlot = ({ slotKey, slotState, uploadMeta, uploading, inputKey, onFileSelect, onRotate }) => {
  const config = SLOT_FIELDS[slotKey];
  const previewUrl = buildVersionedUrl(slotState?.[config.urlKey], uploadMeta?.version);

  return (
    <div className="upload-slot-card" data-testid={`admin-upload-slot-${slotKey}`}>
      <div className="upload-slot-header">
        <strong data-testid={`admin-upload-slot-title-${slotKey}`}>{config.label}</strong>
        {uploadMeta?.orientation_source ? (
          <span className="upload-slot-badge" data-testid={`admin-upload-slot-badge-${slotKey}`}>
            {uploadMeta.orientation_source}
          </span>
        ) : null}
      </div>
      <Input
        key={inputKey}
        type="file"
        accept="image/*"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            onFileSelect(slotKey, file);
          }
          event.target.value = "";
        }}
        data-testid={`admin-upload-input-${slotKey}`}
      />
      {uploading ? <p className="upload-slot-copy">Uploading, correcting orientation, and optimizing…</p> : null}
      {previewUrl ? (
        <div className="upload-slot-preview" data-testid={`admin-upload-preview-${slotKey}`}>
          <img src={previewUrl} alt={config.label} className="upload-slot-image" data-testid={`admin-upload-preview-image-${slotKey}`} />
        </div>
      ) : null}
      {uploadMeta ? (
        <div className="upload-slot-copy" data-testid={`admin-upload-meta-${slotKey}`}>
          {uploadMeta.width} × {uploadMeta.height} · auto {uploadMeta.auto_rotation}° · manual {uploadMeta.manual_rotation}°
        </div>
      ) : null}
      {slotState?.[config.fileIdKey] ? (
        <div className="upload-slot-actions" data-testid={`admin-upload-rotate-actions-${slotKey}`}>
          <Button type="button" variant="outline" onClick={() => onRotate(slotKey, "counterclockwise")} data-testid={`admin-upload-rotate-ccw-${slotKey}`}>
            <RotateCcw size={16} /> Rotate left
          </Button>
          <Button type="button" variant="outline" onClick={() => onRotate(slotKey, "clockwise")} data-testid={`admin-upload-rotate-cw-${slotKey}`}>
            <RotateCw size={16} /> Rotate right
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const initialTestimonialForm = {
  name: "",
  location: "Toronto",
  quote: "",
  rating: 5,
  project_type: "Interior repaint",
};

const initialBlogForm = {
  title: "",
  excerpt: "",
  category: "Painting Tips",
  city: "Toronto",
  read_time: "4 min read",
  hero_image: "",
  body: "",
  published: true,
};

export const AdminDashboard = ({ siteData, refetchSite }) => {
  const [galleryForm, setGalleryForm] = useState(initialGalleryForm);
  const [galleryUploadMeta, setGalleryUploadMeta] = useState({});
  const [activeUploadSlot, setActiveUploadSlot] = useState(null);
  const [activeRotateSlot, setActiveRotateSlot] = useState(null);
  const [uploadInputKeys, setUploadInputKeys] = useState({ image: 0, before_image: 0, after_image: 0 });
  const [testimonialForm, setTestimonialForm] = useState(initialTestimonialForm);
  const [blogForm, setBlogForm] = useState(initialBlogForm);
  const [bookingRulesForm, setBookingRulesForm] = useState({
    minimum_notice_days: siteData?.booking_rules?.minimum_notice_days ?? 4,
  });
  const [bookingRulesSaving, setBookingRulesSaving] = useState(false);
  const uploadRequestIds = useRef({ image: 0, before_image: 0, after_image: 0 });

  useEffect(() => {
    setBookingRulesForm({
      minimum_notice_days: siteData?.booking_rules?.minimum_notice_days ?? 4,
    });
  }, [siteData?.booking_rules?.minimum_notice_days]);

  const { data: dashboard, refetch: refetchDashboard } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: api.getDashboard,
  });

  const { data: leads, refetch: refetchLeads } = useQuery({
    queryKey: ["admin-leads"],
    queryFn: api.getLeads,
  });

  const { data: bookings } = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: api.getBookings,
  });

  const summaryCards = useMemo(
    () => [
      { label: "Total leads", value: dashboard?.total_leads || 0 },
      { label: "Leads today", value: dashboard?.leads_today || 0 },
      { label: "Booked this week", value: dashboard?.booked_this_week || 0 },
      { label: "Estimate visits", value: dashboard?.total_bookings || 0 },
    ],
    [dashboard]
  );

  const refreshAll = async () => {
    await Promise.all([refetchSite?.(), refetchDashboard(), refetchLeads()]);
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await api.updateLeadStatus(id, { status, admin_notes: "Updated from dashboard" });
      toast.success("Lead updated", { description: `Status moved to ${status}.` });
      await refreshAll();
    } catch (error) {
      toast.error("Status update failed", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    }
  };

  const submitGallery = async () => {
    if (!galleryForm.image_url) {
      toast.error("Add a project photo first", {
        description: "Upload the main project image before saving the gallery item.",
      });
      return;
    }
    try {
      await api.createGalleryItem(galleryForm);
      toast.success("Gallery item added", { description: "The homepage gallery is refreshed." });
      setGalleryForm(initialGalleryForm);
      setGalleryUploadMeta({});
      await refreshAll();
    } catch (error) {
      toast.error("Gallery save failed", { description: error?.response?.data?.detail || "Try again shortly." });
    }
  };

  const applyUploadToForm = (slotKey, upload) => {
    const { urlKey, fileIdKey } = SLOT_FIELDS[slotKey];
    setGalleryForm((current) => ({
      ...current,
      [urlKey]: upload.preview_url,
      [fileIdKey]: upload.id,
    }));
    setGalleryUploadMeta((current) => ({ ...current, [slotKey]: upload }));
  };

  const uploadGalleryAsset = async (slotKey, file) => {
    const { urlKey, fileIdKey } = SLOT_FIELDS[slotKey];
    uploadRequestIds.current[slotKey] += 1;
    const requestId = uploadRequestIds.current[slotKey];
    setActiveUploadSlot(slotKey);
    setGalleryForm((current) => ({
      ...current,
      [urlKey]: "",
      [fileIdKey]: null,
    }));
    setGalleryUploadMeta((current) => ({ ...current, [slotKey]: null }));
    try {
      const upload = await api.uploadImageFile(file);
      if (requestId !== uploadRequestIds.current[slotKey]) {
        return;
      }
      applyUploadToForm(slotKey, upload);
      toast.success(`${SLOT_FIELDS[slotKey].label} uploaded`, {
        description: "Orientation correction and optimization were applied automatically.",
      });
    } catch (error) {
      toast.error("Upload failed", {
        description: error?.response?.data?.detail || "Please try another image.",
      });
    } finally {
      if (requestId === uploadRequestIds.current[slotKey]) {
        setActiveUploadSlot(null);
        setUploadInputKeys((current) => ({ ...current, [slotKey]: current[slotKey] + 1 }));
      }
    }
  };

  const rotateGalleryAsset = async (slotKey, direction) => {
    const { fileIdKey, urlKey } = SLOT_FIELDS[slotKey];
    const fileId = galleryForm[fileIdKey];
    if (!fileId) return;
    setActiveRotateSlot(slotKey);
    try {
      const upload = await api.rotateImageFile(fileId, direction);
      setGalleryForm((current) => ({ ...current, [urlKey]: upload.preview_url }));
      setGalleryUploadMeta((current) => ({ ...current, [slotKey]: upload }));
      toast.success("Image rotated", { description: "The corrected orientation has been refreshed." });
    } catch (error) {
      toast.error("Rotate failed", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setActiveRotateSlot(null);
    }
  };

  const rotateExistingGalleryImage = async (item, direction) => {
    if (!item.image_file_id) {
      toast.error("This photo isn't in the new upload pipeline yet", {
        description: "Upload it again through the admin uploader to unlock one-click rotate controls.",
      });
      return;
    }
    try {
      await api.rotateImageFile(item.image_file_id, direction);
      toast.success("Gallery image rotated", {
        description: "The live site will now use the updated orientation.",
      });
      await refreshAll();
    } catch (error) {
      toast.error("Rotate failed", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    }
  };

  const submitTestimonial = async () => {
    try {
      await api.createTestimonial({ ...testimonialForm, rating: Number(testimonialForm.rating) });
      toast.success("Testimonial added", { description: "Social proof updated successfully." });
      setTestimonialForm(initialTestimonialForm);
      await refreshAll();
    } catch (error) {
      toast.error("Testimonial save failed", { description: error?.response?.data?.detail || "Try again shortly." });
    }
  };

  const submitBlog = async () => {
    try {
      await api.createBlogPost(blogForm);
      toast.success("Blog post added", { description: "The SEO feed is live." });
      setBlogForm(initialBlogForm);
      await refreshAll();
    } catch (error) {
      toast.error("Blog save failed", { description: error?.response?.data?.detail || "Try again shortly." });
    }
  };

  const saveBookingRules = async () => {
    const minimumNoticeDays = Number(bookingRulesForm.minimum_notice_days);
    if (!Number.isInteger(minimumNoticeDays) || minimumNoticeDays < 1) {
      toast.error("Enter a valid notice period", {
        description: "Minimum notice must be at least 1 calendar day.",
      });
      return;
    }

    setBookingRulesSaving(true);
    try {
      await api.updateBookingRules({ minimum_notice_days: minimumNoticeDays });
      toast.success("Booking notice updated", {
        description: `Customers now need at least ${minimumNoticeDays} calendar days of notice.`,
      });
      await refreshAll();
    } catch (error) {
      toast.error("Couldn't update booking rules", {
        description: error?.response?.data?.detail || "Please try again shortly.",
      });
    } finally {
      setBookingRulesSaving(false);
    }
  };

  return (
    <div className="detail-page admin-page" data-testid="admin-dashboard-page">
      <div className="detail-card admin-card-shell">
        <div className="admin-topbar" data-testid="admin-topbar">
          <div>
            <Link to="/" className="detail-back-link" data-testid="admin-back-home-link">
              <ArrowLeft size={16} /> Back to website
            </Link>
            <p className="detail-eyebrow">Admin dashboard</p>
            <h1 data-testid="admin-heading">Lead command center.</h1>
          </div>
          <div className="admin-contact-card" data-testid="admin-contact-card">
            <PhoneCall size={18} />
            <span data-testid="admin-contact-copy">Every lead, booking, testimonial, project image, and blog post in one place.</span>
          </div>
        </div>

        <div className="admin-summary-grid" data-testid="admin-summary-grid">
          {summaryCards.map((card) => (
            <Card key={card.label} className="admin-summary-card" data-testid={`admin-summary-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <CardContent className="admin-summary-content">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="admin-chart-shell" data-testid="admin-chart-shell">
          <h3 data-testid="admin-chart-heading">Lead sources</h3>
          <div className="admin-chart" data-testid="admin-source-chart">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dashboard?.leads_by_source || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="source" stroke="rgba(244,239,229,0.7)" />
                <YAxis stroke="rgba(244,239,229,0.7)" />
                <Tooltip />
                <Bar dataKey="count" fill="#c76b4f" radius={[12, 12, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <Tabs defaultValue="leads" className="admin-tabs" data-testid="admin-tabs-root">
          <TabsList className="admin-tabs-list" data-testid="admin-tabs-list">
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="admin-tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" data-testid="admin-leads-panel">
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(leads || []).map((lead) => (
                  <TableRow key={lead.id} data-testid={`admin-lead-row-${lead.id}`}>
                    <TableCell>
                      <div className="admin-table-stack">
                        <strong>{lead.name}</strong>
                        <span>{lead.email} · {lead.phone}</span>
                      </div>
                    </TableCell>
                    <TableCell>{lead.source}</TableCell>
                    <TableCell>
                      <div className="admin-table-stack">
                        <strong>{lead.service_type}</strong>
                        <span>{lead.city} · {lead.timeline}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={lead.status} onValueChange={(value) => updateLeadStatus(lead.id, value)}>
                        <SelectTrigger className="max-w-[180px]" data-testid={`admin-lead-status-trigger-${lead.id}`}>
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new" data-testid={`admin-lead-status-new-${lead.id}`}>New</SelectItem>
                          <SelectItem value="quoted" data-testid={`admin-lead-status-quoted-${lead.id}`}>Quoted</SelectItem>
                          <SelectItem value="scheduled" data-testid={`admin-lead-status-scheduled-${lead.id}`}>Scheduled</SelectItem>
                          <SelectItem value="won" data-testid={`admin-lead-status-won-${lead.id}`}>Won</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="gallery" data-testid="admin-gallery-panel">
            <div className="admin-form-shell">
              <div className="admin-form-heading"><ImagePlus size={18} /> Add gallery item</div>
              <div className="quote-grid quote-grid-wide">
                <Input placeholder="Project title" value={galleryForm.title} onChange={(event) => setGalleryForm((current) => ({ ...current, title: event.target.value }))} data-testid="admin-gallery-title-input" />
                <Input placeholder="Location" value={galleryForm.location} onChange={(event) => setGalleryForm((current) => ({ ...current, location: event.target.value }))} data-testid="admin-gallery-location-input" />
                <Input placeholder="Category" value={galleryForm.category} onChange={(event) => setGalleryForm((current) => ({ ...current, category: event.target.value }))} data-testid="admin-gallery-category-input" />
                <Input placeholder="Room type" value={galleryForm.room_type} onChange={(event) => setGalleryForm((current) => ({ ...current, room_type: event.target.value }))} data-testid="admin-gallery-room-type-input" />
                <Input placeholder="Accent label" value={galleryForm.accent} onChange={(event) => setGalleryForm((current) => ({ ...current, accent: event.target.value }))} data-testid="admin-gallery-accent-input" />
              </div>
              <div className="admin-upload-grid" data-testid="admin-upload-grid">
                {Object.keys(SLOT_FIELDS).map((slotKey) => (
                  <UploadSlot
                    key={slotKey}
                    slotKey={slotKey}
                    slotState={galleryForm}
                    uploadMeta={galleryUploadMeta[slotKey]}
                    uploading={activeUploadSlot === slotKey || activeRotateSlot === slotKey}
                    inputKey={`${slotKey}-${uploadInputKeys[slotKey]}`}
                    onFileSelect={uploadGalleryAsset}
                    onRotate={rotateGalleryAsset}
                  />
                ))}
              </div>
              <Textarea placeholder="Project description" value={galleryForm.description} onChange={(event) => setGalleryForm((current) => ({ ...current, description: event.target.value }))} data-testid="admin-gallery-description-input" />
              <Button className="cta-primary max-w-fit" onClick={submitGallery} data-testid="admin-gallery-submit-button">Save gallery item</Button>
            </div>
            <div className="admin-chip-list" data-testid="admin-gallery-chip-list">
              {(siteData?.gallery || []).map((item) => (
                <div className="admin-chip-card" key={item.id} data-testid={`admin-gallery-chip-${item.id}`}>
                  <div className="admin-gallery-chip-content">
                    <img src={item.image_url} alt={item.title} className="admin-gallery-chip-image" data-testid={`admin-gallery-chip-image-${item.id}`} />
                    <div className="admin-gallery-chip-copy">
                      <strong>{item.title}</strong>
                      <span>{item.category} · {item.room_type}</span>
                    </div>
                  </div>
                  <div className="admin-gallery-chip-actions">
                    <Button variant="ghost" onClick={() => rotateExistingGalleryImage(item, "counterclockwise")} data-testid={`admin-gallery-rotate-left-button-${item.id}`}>
                      <RotateCcw size={16} />
                    </Button>
                    <Button variant="ghost" onClick={() => rotateExistingGalleryImage(item, "clockwise")} data-testid={`admin-gallery-rotate-right-button-${item.id}`}>
                      <RotateCw size={16} />
                    </Button>
                    <Button variant="ghost" onClick={() => api.deleteGalleryItem(item.id).then(refreshAll)} data-testid={`admin-gallery-delete-button-${item.id}`}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="testimonials" data-testid="admin-testimonials-panel">
            <div className="admin-form-shell">
              <div className="admin-form-heading"><MessageSquareText size={18} /> Add testimonial</div>
              <div className="quote-grid quote-grid-wide">
                <Input placeholder="Client name" value={testimonialForm.name} onChange={(event) => setTestimonialForm((current) => ({ ...current, name: event.target.value }))} data-testid="admin-testimonial-name-input" />
                <Input placeholder="Location" value={testimonialForm.location} onChange={(event) => setTestimonialForm((current) => ({ ...current, location: event.target.value }))} data-testid="admin-testimonial-location-input" />
                <Input placeholder="Project type" value={testimonialForm.project_type} onChange={(event) => setTestimonialForm((current) => ({ ...current, project_type: event.target.value }))} data-testid="admin-testimonial-project-type-input" />
                <Input type="number" placeholder="Rating" value={testimonialForm.rating} onChange={(event) => setTestimonialForm((current) => ({ ...current, rating: event.target.value }))} data-testid="admin-testimonial-rating-input" />
              </div>
              <Textarea placeholder="Client quote" value={testimonialForm.quote} onChange={(event) => setTestimonialForm((current) => ({ ...current, quote: event.target.value }))} data-testid="admin-testimonial-quote-input" />
              <Button className="cta-primary max-w-fit" onClick={submitTestimonial} data-testid="admin-testimonial-submit-button">Save testimonial</Button>
            </div>
            <div className="admin-chip-list" data-testid="admin-testimonial-chip-list">
              {(siteData?.testimonials || []).map((item) => (
                <div className="admin-chip-card" key={item.id} data-testid={`admin-testimonial-chip-${item.id}`}>
                  <span>{item.name}</span>
                  <Button variant="ghost" onClick={() => api.deleteTestimonial(item.id).then(refreshAll)} data-testid={`admin-testimonial-delete-button-${item.id}`}>Delete</Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="blog" data-testid="admin-blog-panel">
            <div className="admin-form-shell">
              <div className="admin-form-heading"><Newspaper size={18} /> Add blog post</div>
              <div className="quote-grid quote-grid-wide">
                <Input placeholder="Title" value={blogForm.title} onChange={(event) => setBlogForm((current) => ({ ...current, title: event.target.value }))} data-testid="admin-blog-title-input" />
                <Input placeholder="Excerpt" value={blogForm.excerpt} onChange={(event) => setBlogForm((current) => ({ ...current, excerpt: event.target.value }))} data-testid="admin-blog-excerpt-input" />
                <Input placeholder="Category" value={blogForm.category} onChange={(event) => setBlogForm((current) => ({ ...current, category: event.target.value }))} data-testid="admin-blog-category-input" />
                <Input placeholder="City" value={blogForm.city} onChange={(event) => setBlogForm((current) => ({ ...current, city: event.target.value }))} data-testid="admin-blog-city-input" />
                <Input placeholder="Read time" value={blogForm.read_time} onChange={(event) => setBlogForm((current) => ({ ...current, read_time: event.target.value }))} data-testid="admin-blog-read-time-input" />
                <Input placeholder="Hero image URL" value={blogForm.hero_image} onChange={(event) => setBlogForm((current) => ({ ...current, hero_image: event.target.value }))} data-testid="admin-blog-image-input" />
              </div>
              <Textarea placeholder="Body copy" className="min-h-[180px]" value={blogForm.body} onChange={(event) => setBlogForm((current) => ({ ...current, body: event.target.value }))} data-testid="admin-blog-body-input" />
              <Button className="cta-primary max-w-fit" onClick={submitBlog} data-testid="admin-blog-submit-button">Save blog post</Button>
            </div>
            <div className="admin-chip-list" data-testid="admin-blog-chip-list">
              {(siteData?.blog_posts || []).map((item) => (
                <div className="admin-chip-card" key={item.id} data-testid={`admin-blog-chip-${item.id}`}>
                  <span>{item.title}</span>
                  <Button variant="ghost" onClick={() => api.deleteBlogPost(item.id).then(refreshAll)} data-testid={`admin-blog-delete-button-${item.id}`}>Delete</Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="bookings" data-testid="admin-bookings-panel">
            <div className="admin-form-shell" data-testid="admin-booking-rules-card">
              <div className="admin-form-heading">Rolling booking notice</div>
              <p className="upload-slot-copy" data-testid="admin-booking-rules-copy">
                Customers can book any future date starting from the minimum notice window below. Earlier dates stay unavailable automatically.
              </p>
              <div className="quote-grid quote-grid-wide admin-booking-rule-grid">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={bookingRulesForm.minimum_notice_days}
                  onChange={(event) => setBookingRulesForm({ minimum_notice_days: event.target.value })}
                  data-testid="admin-booking-notice-days-input"
                />
                <Button className="cta-primary max-w-fit" onClick={saveBookingRules} disabled={bookingRulesSaving} data-testid="admin-booking-notice-save-button">
                  {bookingRulesSaving ? "Saving…" : "Save minimum notice"}
                </Button>
              </div>
            </div>
            <Table data-testid="admin-bookings-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(bookings || []).map((booking) => (
                  <TableRow key={booking.id} data-testid={`admin-booking-row-${booking.id}`}>
                    <TableCell>
                      <div className="admin-table-stack">
                        <strong>{booking.name}</strong>
                        <span>{booking.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>{booking.date} · {booking.time_label}</TableCell>
                    <TableCell>{booking.service_type}</TableCell>
                    <TableCell>{booking.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
